import { useMemo } from "react";
import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  XYChart,
  BarStack,
  BarGroup,
  BarSeries,
  Axis,
  ChartGrid,
  ChartTooltip,
  angledTickLabelProps,
  LegendOrdinal,
  scaleOrdinal,
  compactNumber,
  useChartColors,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  truncateLabel,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { useStageThroughput, THROUGHPUT_SERIES, type ThroughputRow } from "./use-stage-throughput";

/**
 * `stage-throughput` — a themed multi-series bar chart of projects per stage split
 * by age health, on the `XYChart` scaffold. It pressure-tests the scaffold's
 * stacked/grouped path AND a rich settings schema: a workflow `select`
 * (`dynamicOptions`), pipeline-vs-volume ordering, a top-N `number`, per-series
 * toggles, and a stacked/grouped mode — every field drives the render. The data
 * layer fans `useIssueGroupCounts` across the three age statuses (see
 * `use-stage-throughput`).
 */
export const stageThroughputConfigSchema = z
  .looseObject({
    workflow: z.string().nullable().default(null),
    sort: z.enum(["pipeline", "volume"]).default("pipeline"),
    maxStages: z.int().min(3).max(20).default(8),
    showOnTrack: z.boolean().default(true),
    showAtRisk: z.boolean().default(true),
    showOffTrack: z.boolean().default(true),
    showUntracked: z.boolean().default(true),
    mode: z.enum(["stacked", "grouped"]).default("stacked"),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    legend: z.boolean().default(true),
  });

export type StageThroughputConfig = z.infer<typeof stageThroughputConfigSchema>;

const VISIBLE_KEY = {
  onTrack: "showOnTrack",
  atRisk: "showAtRisk",
  offTrack: "showOffTrack",
  untracked: "showUntracked",
} as const;

const xAccessor = (d: ThroughputRow) => d.label;

function StageThroughputRender({ h, config }: WidgetRenderContext<StageThroughputConfig>) {
  const { data, loading, error } = useStageThroughput(config.workflow);
  const colors = useChartColors();
  // Click-through: a bar press opens this card's Data drawer on the stage's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  const series = useMemo(
    () => THROUGHPUT_SERIES.filter((s) => config[VISIBLE_KEY[s.key]] !== false),
    [config],
  );

  const rows = useMemo(() => {
    const sorted =
      config.sort === "volume" ? [...data].sort((a, b) => b.total - a.total) : data;
    return sorted.slice(0, Math.max(3, config.maxStages));
  }, [data, config.sort, config.maxStages]);

  const legendScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: series.map((s) => s.label),
        range: series.map((s) => colors.resolve(s.colorToken) ?? colors.ramp[0]),
      }),
    [series, colors],
  );

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }
  if (rows.length === 0 || series.length === 0) {
    return <WidgetEmpty icon="bar-chart-3" title="No projects in range" />;
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;
  const showLegend = config.legend && !compact;

  const bars = series.map((s) => (
    <BarSeries
      key={s.key}
      dataKey={s.label}
      data={rows}
      xAccessor={xAccessor}
      yAccessor={(d: ThroughputRow) => d[s.key]}
      colorToken={s.colorToken}
    />
  ));

  return (
    <Box direction="column" fill="flex" gap="xs" cursor={drill && instance ? "pointer" : undefined}>
      <XYChart<ThroughputRow>
        xScale={{ type: "band", paddingInner: 0.3, paddingOuter: 0.2 }}
        yScale={{ type: "linear", nice: true }}
        margin={{ top: 8, right: 8, bottom: showAxes ? 52 : 8, left: showAxes ? 32 : 8 }}
        ariaLabel="Projects by stage and age health"
        onPointerUp={
          drill && instance
            ? ({ datum }) => {
                if (datum?.id)
                  drill.openData(instance.instanceId, {
                    kind: "dimension",
                    dimension: "stage",
                    ids: [datum.id],
                    label: datum.label,
                  });
              }
            : undefined
        }
      >
        {showGrid ? <ChartGrid rows columns={false} numTicks={4} /> : null}
        {showAxes ? <Axis orientation="left" numTicks={4} tickFormat={compactNumber} /> : null}
        {showAxes ? (
          <Axis
            orientation="bottom"
            tickFormat={(v) => truncateLabel(String(v))}
            tickLabelProps={angledTickLabelProps}
          />
        ) : null}
        {config.mode === "grouped" ? <BarGroup>{bars}</BarGroup> : <BarStack>{bars}</BarStack>}
        <ChartTooltip
          snapTooltipToDatumX
          showVerticalCrosshair
          renderTooltip={({ tooltipData }) => {
            const d = tooltipData?.nearestDatum?.datum as ThroughputRow | undefined;
            if (!d) return null;
            return (
              <WidgetChartTooltip
                label={d.label}
                details={series.map((s) => `${s.label}: ${d[s.key]}`)}
                footer={`${d.total} total`}
              />
            );
          }}
        />
      </XYChart>
      {showLegend ? (
        <Box direction="row" justify="center">
          <LegendOrdinal scale={legendScale} direction="row" itemMargin="0 10px 0 0" shapeMargin="0 5px 0 0" />
        </Box>
      ) : null}
    </Box>
  );
}

export const stageThroughputDef: WidgetModule<StageThroughputConfig> = {
  type: "stage-throughput",
  title: "Stage throughput",
  icon: "bar-chart-3",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 8, defaultW: 6, defaultH: 4 },
  configSchema: stageThroughputConfigSchema,
  headerActions: WidgetHeaderFilters,
  settings: [
    {
      title: "Source",
      fields: [
        {
          key: "workflow",
          kind: "select",
          label: "Workflow",
          description: "Limit to one workflow, or leave clear for all.",
          dynamicOptions: "workflows",
          clearable: true,
        },
      ],
    },
    {
      title: "Data",
      fields: [
        {
          key: "sort",
          kind: "segmented",
          label: "Order",
          options: [
            { value: "pipeline", label: "Pipeline" },
            { value: "volume", label: "Volume" },
          ],
        },
        { key: "maxStages", kind: "number", label: "Max stages", min: 3, max: 20, step: 1 },
      ],
    },
    {
      title: "Series",
      fields: [
        { key: "showOnTrack", kind: "toggle", label: "On track" },
        { key: "showAtRisk", kind: "toggle", label: "At risk" },
        { key: "showOffTrack", kind: "toggle", label: "Off track" },
        { key: "showUntracked", kind: "toggle", label: "Untracked" },
      ],
    },
    {
      title: "Display",
      fields: [
        {
          key: "mode",
          kind: "segmented",
          label: "Bars",
          options: [
            { value: "stacked", label: "Stacked" },
            { value: "grouped", label: "Grouped" },
          ],
        },
        { key: "showGrid", kind: "toggle", label: "Gridlines" },
        { key: "showAxes", kind: "toggle", label: "Axes" },
        { key: "legend", kind: "toggle", label: "Legend" },
      ],
    },
  ],
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "stage-throughput.default",
      label: "Stage throughput",
      description: "Projects per stage, stacked or grouped by age health.",
      section: "Charts",
      tags: ["projects", "stages", "workflows", "ageHealth"] satisfies readonly WidgetTag[],
      defaultConfig: { sort: "pipeline", maxStages: 8, mode: "stacked", legend: true },
      defaultW: 6,
      defaultH: 4,
    },
  ],
  render: StageThroughputRender,
};
